/**
 * <s-newsletter-popup> — timed newsletter sign-up dialog.
 *
 * Opens its child <dialog data-popup-dialog> after `data-delay` ms, once per visitor.
 * Closing snoozes it for `data-snooze-days`; submitting the form stops it for good.
 * After the customer form posts back (?customer_posted=true) the dialog reopens
 * immediately so the visitor sees the welcome code. `data-preview` (theme editor)
 * always opens and stores nothing.
 */
const STORAGE_KEY = 'zalooq:newsletter-popup';
const DAY_MS = 86400000;

class SNewsletterPopup extends HTMLElement {
  #timer = 0;
  #ac;

  connectedCallback() {
    this.dialog = this.querySelector('[data-popup-dialog]');
    if (!this.dialog) return;

    this.addEventListener('click', this.#onClick);
    this.dialog.addEventListener('close', this.#onClose);
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit);

    const posted = new URLSearchParams(location.search).get('customer_posted') === 'true';
    const ownPost = this.#read().pending === true;
    const hasFeedback = this.querySelector('[data-popup-success], [aria-invalid="true"]');

    if (ownPost && (posted || hasFeedback)) {
      this.#write({ pending: false, subscribed: Boolean(this.querySelector('[data-popup-success]')) });
      this.#open();
      return;
    }
    if (!this.hasAttribute('data-preview') && !this.#shouldShow()) return;

    const delay = Number(this.dataset.delay) || 6000;
    const start = () => { this.#timer = window.setTimeout(() => this.#open(), delay); };
    // Phones: wait for the shopper's first scroll before starting the timer, so the popup
    // never lands during the initial load. Desktop and the theme editor keep the plain timer.
    const phone = matchMedia('(max-width: 767px)').matches;
    if (phone && !this.hasAttribute('data-preview')) {
      this.#ac = new AbortController();
      window.addEventListener('scroll', () => { this.#ac?.abort(); start(); }, { once: true, passive: true, signal: this.#ac.signal });
    } else {
      start();
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  #shouldShow() {
    const state = this.#read();
    if (state.subscribed) return false;
    return !state.snoozedUntil || Date.now() > state.snoozedUntil;
  }

  #open() {
    if (this.dialog.open || document.querySelector('dialog[open]')) return;
    this.dialog.showModal();
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target === this.dialog || target.closest('[data-popup-close]')) {
      this.dialog.close();
      return;
    }

    const copy = target.closest('[data-copy-code]');
    if (copy instanceof HTMLElement) {
      navigator.clipboard?.writeText(copy.dataset.copyCode || '').then(() => {
        copy.textContent = copy.dataset.copiedLabel || copy.textContent;
      });
    }
  };

  #onClose = () => {
    if (this.hasAttribute('data-preview')) return;
    const state = this.#read();
    if (state.subscribed || state.pending) return;
    const days = Number(this.dataset.snoozeDays) || 14;
    this.#write({ ...state, snoozedUntil: Date.now() + days * DAY_MS });
  };

  #onSubmit = () => {
    if (this.hasAttribute('data-preview')) return;
    this.#write({ ...this.#read(), pending: true });
  };

  #read() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  }

  #write(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage unavailable: popup simply shows again next visit */
    }
  }
}

customElements.define('s-newsletter-popup', SNewsletterPopup);
